import { JobStatus, Patient, v2 } from '../../..';
import { getHcpAttributeNames } from './helpers/getHcpAttributeNames';

export type PersonJobStatusAdapterParts = Pick<
    Patient,
    | 'madeCurrentDateTime'
    | 'arrivedDateTime'
    | 'finishedDateTime'
    | 'hcpAbortedDateTime'
    | 'abortedBy'
    | 'jobStatusBeforeAbort'
    | 'controllerAbortedReason'
    | 'controllerAbortedNotes'
    | 'controllerReverseReason'
    | 'controllerReverseNotes'
    | 'hcpAbortedReason'
    | 'hcpAbortedNotes'
    | 'hcpReverseReason'
    | 'hcpReverseNotes'
    | 'postVisitNotes'
    | 'buddyId'
    | 'buddyName'
    | 'buddyGrade'
    | 'buddyMadeCurrentDateTime'
    | 'buddyArrivedDateTime'
    | 'buddyFinishedDateTime'
    | 'buddyHcpAbortedDateTime'
    | 'buddyAbortedBy'
    | 'buddyJobStatus'
    | 'buddyJobStatusBeforeAbort'
    | 'buddyControllerAbortedReason'
    | 'buddyControllerAbortedNotes'
    | 'buddyControllerReverseReason'
    | 'buddyControllerReverseNotes'
    | 'buddyHcpAbortedReason'
    | 'buddyHcpAbortedNotes'
    | 'buddyHcpReverseReason'
    | 'buddyHcpReverseNotes'
    | 'postVisitNotesBuddy'
    | 'abortedFollowUpAction'
    | 'withdrawnReason'
    | 'withdrawnNotes'
> & {
    jobStatus?: JobStatus;
};

type PersonJobStatusAdapterOptions = {
    isBuddy: boolean;
    isControllerReverse: boolean;
    isHcpReverse: boolean;
    previousStatus: JobStatus;
    isLatestStatus: boolean;
};

const getReasonAttributeName = (
    status: JobStatus,
    options: PersonJobStatusAdapterOptions,
    isReverse?: boolean,
) => {
    const { isBuddy, isControllerReverse, isHcpReverse } = options;

    const {
        controllerReverseReason,
        hcpReverseReason,
        controllerAbortedReason,
        hcpAbortedReason,
        withdrawnReason,
    } = getHcpAttributeNames(isBuddy);

    if (status === JobStatus.WITHDRAWN) {
        return withdrawnReason;
    }
    if (status === JobStatus.CONTROLLER_ABORTED) {
        return controllerAbortedReason;
    }
    if (status === JobStatus.HCP_ABORTED) {
        return hcpAbortedReason;
    }
    if (isControllerReverse && isReverse) {
        return controllerReverseReason;
    }
    if (isHcpReverse && isReverse) {
        return hcpReverseReason;
    }

    return null;
};

const getNoteAttributeName = (
    status: JobStatus,
    options: PersonJobStatusAdapterOptions,
    isReverse?: boolean,
) => {
    const { isBuddy, isControllerReverse, isHcpReverse } = options;

    const {
        controllerReverseNotes,
        hcpReverseNotes,
        controllerAbortedNotes,
        hcpAbortedNotes,
        withdrawnNotes,
    } = getHcpAttributeNames(isBuddy);

    if (status === JobStatus.WITHDRAWN) {
        return withdrawnNotes;
    }
    if (status === JobStatus.CONTROLLER_ABORTED) {
        return controllerAbortedNotes;
    }
    if (status === JobStatus.HCP_ABORTED) {
        return hcpAbortedNotes;
    }
    if (isControllerReverse && isReverse) {
        return controllerReverseNotes;
    }
    if (isHcpReverse && isReverse) {
        return hcpReverseNotes;
    }

    return null;
};

const getDateAttributeName = (status: JobStatus, options: PersonJobStatusAdapterOptions) => {
    const { isBuddy, isLatestStatus } = options;

    const {
        arrivedDateTime,
        madeCurrentDateTime,
        finishedDateTime,
        hcpAbortedDateTime,
    } = getHcpAttributeNames(isBuddy);

    switch (status) {
        case JobStatus.ARRIVED:
            return arrivedDateTime;
        case JobStatus.CURRENT:
            return madeCurrentDateTime;
        case JobStatus.COMPLETED:
            return finishedDateTime;
        case JobStatus.HCP_ABORTED:
            return hcpAbortedDateTime;
        case JobStatus.CONTROLLER_ABORTED:
            if (isLatestStatus) {
                return finishedDateTime;
            }
            return null;
        default:
            return null;
    }
};

export const personJobStatusAdapter = (
    personJobStatus: v2.PersonJobStatus,
    options: PersonJobStatusAdapterOptions,
): PersonJobStatusAdapterParts => {
    const {
        jobTypeStatusId,
        jobTypeStatus,
        jobStatusReason,
        note,
        statusDateTime,
        followUpAction,
    } = personJobStatus;

    const { isBuddy, previousStatus } = options;

    const key = jobTypeStatus?.key;
    const { reason, isReverse } = jobStatusReason || {};
    const status = (key || jobTypeStatusId) as JobStatus;
    const isAborted = status === JobStatus.HCP_ABORTED || status === JobStatus.CONTROLLER_ABORTED;

    const { jobStatus, jobStatusBeforeAbort } = getHcpAttributeNames(isBuddy);
    const dateAttributeName = getDateAttributeName(status, options);
    const reasonAttributeName = getReasonAttributeName(status, options, isReverse);
    const noteAttributeName = getNoteAttributeName(status, options, isReverse);

    const result: PersonJobStatusAdapterParts = {
        [jobStatus]: status,
    };

    if (dateAttributeName) {
        result[dateAttributeName] = new Date(statusDateTime).toISOString();
    }
    if (reasonAttributeName && reason) {
        result[reasonAttributeName] = reason;
    }
    if (noteAttributeName && note) {
        result[noteAttributeName] = note;
    }
    if (isAborted && previousStatus) {
        result[jobStatusBeforeAbort] = previousStatus;
    }
    if (isAborted && followUpAction) {
        result.abortedFollowUpAction = followUpAction;
    }

    return result;
};
